import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { Button, Card } from '../../ui';
import { ProcessorStatePanel, useProcessorStates } from '../../ProcessorStateBadge';

const STATUS_URL = '/ui/admin/caf-inbox-processor';

const PROCESS_ENTIRE_INBOX_CONFIRM =
  'Process the ENTIRE unread inbox? This may take a long time and process tens of thousands of emails.';

export const CafInboxPanel: React.FC = () => {
  const { apiRequest } = useAuth();
  const queryClient = useQueryClient();
  const states = useProcessorStates();

  const [mode, setMode] = useState<'regular' | 'test' | 'runonce'>('test');
  const [message, setMessage] = useState<string | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);

  // isEnabled is fetched once at mount and re-fetched after enable/disable,
  // matching the old panel. The 10s live-state cadence lives in the
  // ProcessorStatePanel below (console-wide useProcessorStates() poll), not
  // here.
  const statusQuery = useQuery({
    queryKey: ['caf-inbox-status'],
    queryFn: () => apiRequest<{ isEnabled: boolean }>({ url: STATUS_URL, method: 'GET' }),
  });
  const status = statusQuery.data;
  const refreshStatus = () => queryClient.invalidateQueries({ queryKey: ['caf-inbox-status'] });

  const toggleProcessor = async (enable: boolean) => {
    try {
      await apiRequest({
        url: `/ui/admin/caf-inbox-processor/${enable ? 'enable' : 'disable'}`,
        method: 'POST',
        ...(enable ? { data: { mode } } : {}),
      });
      if (enable) {
        const modeText = mode === 'test' ? 'TEST MODE (1 email then pause)'
          : mode === 'runonce' ? 'RUN ONCE MODE (1 email then pause)'
          : 'REGULAR MODE (all emails)';
        setMessage(`CAF Inbox processor enabled in ${modeText}`);
      } else {
        setMessage('CAF Inbox processor disabled');
      }
      refreshStatus();
    } catch {
      setMessage(`Failed to ${enable ? 'enable' : 'disable'} CAF Inbox processor`);
    }
  };

  const runCAFInboxBatch = async (limit: number | 'all') => {
    if (batchRunning) return;
    const label = limit === 'all' ? 'entire inbox' : `${limit} emails`;
    if (limit === 'all' && !window.confirm(PROCESS_ENTIRE_INBOX_CONFIRM)) return;
    setBatchRunning(true);
    setMessage(`Starting CAF inbox batch run (${label})...`);
    try {
      await apiRequest({ url: '/ui/admin/caf-inbox-processor/run-batch', method: 'POST', data: { limit } });
      setMessage(`CAF inbox batch (${label}) started — running in the background. Watch the backend logs / status card for progress.`);
    } catch {
      setMessage(`Failed to start CAF inbox batch run (${label})`);
    } finally {
      setBatchRunning(false);
    }
  };

  return (
    <Card title="CAF Inbox" actions={
      <>
        <Button size="sm" variant="secondary" disabled={status?.isEnabled} onClick={() => toggleProcessor(true)}>Enable CAF Inbox Processor</Button>
        <Button size="sm" variant="ghost" disabled={!status?.isEnabled} onClick={() => toggleProcessor(false)}>Disable CAF Inbox Processor</Button>
      </>
    }>
      <p className="pc-desc">Processes incoming CAF inbox emails to automatically update claim statuses and extract relevant information.</p>

      <div className="pc-mode-select">
        <label htmlFor="cafInboxMode">Processing Mode:</label>
        <select id="cafInboxMode" value={mode} disabled={status?.isEnabled}
                onChange={(e) => setMode(e.target.value as 'regular' | 'test' | 'runonce')}>
          <option value="test">Test Mode (1 email then pause)</option>
          <option value="runonce">Run Once Mode (1 email then pause)</option>
          <option value="regular">Regular Mode (all emails, production)</option>
        </select>
      </div>

      <div className="pc-actions">
        <Button variant="secondary" disabled={batchRunning} onClick={() => runCAFInboxBatch(10)}>Process 10 Emails</Button>
        <Button variant="secondary" disabled={batchRunning} onClick={() => runCAFInboxBatch(1000)}>Process 1000 Emails</Button>
        <Button variant="danger" disabled={batchRunning} onClick={() => runCAFInboxBatch('all')}>Process Entire Inbox</Button>
      </div>

      {message && <p className="pc-desc">{message}</p>}

      <ProcessorStatePanel processorId="caf_inbox" state={states.caf_inbox} />
    </Card>
  );
};
